import { lazy, Suspense, useCallback, useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight, LogOut } from 'lucide-react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import {
  type DevModeOverride,
  useAppMode,
  useDevModeOverride,
  useSetAppMode,
  useSetDevModeOverride,
} from '../../stores/appStore'
import { useCallStore } from '../../stores/callStore'
import { logout } from '../../services/authService'
import { SkipLink } from '../../components/accessibility/SkipLink'
import { PersistentCallBar } from '../../components/PersistentCallBar'
import { APP_PATHS } from '../router/routes'
import { AppRoutes } from '../router/AppRoutes'
import { MobileHeader, MobileMenu } from './MobileMenu'
import { ModeSwitcher } from './ModeSwitcher'
import { PageLoader } from './PageLoader'
import { Navigation } from './Sidebar'

const SIDEBAR_EXPANDED_WIDTH = 260
const SIDEBAR_COLLAPSED_WIDTH = 60
const SIDEBAR_COLLAPSE_KEY = 'reeva_sidebar_collapsed'
const ConsumerWorkspace = lazy(() => import('../../pages/consumer/workspace/ConsumerWorkspace'))

export function AppLayout() {
  const mode = useAppMode()
  const setDevModeOverride = useSetDevModeOverride()
  const setMode = useSetAppMode()
  const devModeOverride = useDevModeOverride()
  const activeCall = useCallStore((s) => s.activeCall)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()

  // Show persistent call bar on non-home pages when a call is active
  // Hide on /line (My Calls) since it has its own live call banner
  const isOnHomePage = location.pathname === '/'
  const isOnMyCalls = location.pathname === '/line'
  const showCallBar = activeCall && !isOnHomePage && !isOnMyCalls
  const isWorkspaceRoute = mode === 'individual' && (
    ['/', '/line', '/tasks', '/calls', '/my-number'].includes(location.pathname) ||
    location.pathname.startsWith('/settings') ||
    location.pathname.startsWith('/memory') ||
    location.pathname === '/calendar' ||
    location.pathname === '/info'
  )

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSE_KEY) === 'true'
    } catch {
      return false
    }
  })

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev
      try { localStorage.setItem(SIDEBAR_COLLAPSE_KEY, String(next)) } catch {}
      return next
    })
  }, [])

  const isCollapsed = mode === 'individual' && sidebarCollapsed
  const sidebarWidth = mode === 'individual'
    ? (isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH)
    : SIDEBAR_EXPANDED_WIDTH

  useEffect(() => { setIsMobileMenuOpen(false) }, [location.pathname])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const modeParam = params.get('mode')
    if (modeParam === 'individual' || modeParam === 'business' || modeParam === 'both') {
      setDevModeOverride(modeParam as DevModeOverride)
      if (modeParam === 'individual' || modeParam === 'business') setMode(modeParam)
    }
  }, [setDevModeOverride, setMode])

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : ''
    document.body.classList.toggle('mobile-menu-open', isMobileMenuOpen)
    return () => { document.body.style.overflow = ''; document.body.classList.remove('mobile-menu-open') }
  }, [isMobileMenuOpen])

  useEffect(() => {
    const handler = () => setIsMobileMenuOpen(true)
    window.addEventListener('open-mobile-menu', handler)
    return () => window.removeEventListener('open-mobile-menu', handler)
  }, [])

  return (
    <>
      {!isWorkspaceRoute && showCallBar && (
        <PersistentCallBar
          taskId={activeCall.taskId}
          company={activeCall.company}
          status={activeCall.status}
          duration={Math.floor((Date.now() - activeCall.startedAt) / 1000)}
          onNavigateToCall={() => navigate('/')}
        />
      )}

      {devModeOverride && (
        <div style={{
          position: 'fixed', top: '0.5rem', right: '0.5rem',
          background: '#f59e0b', color: 'black', fontSize: '0.65rem', fontWeight: '600',
          padding: '0.25rem 0.5rem', borderRadius: '4px', zIndex: 9999,
        }}>
          DEV: {devModeOverride}
        </div>
      )}

      <SkipLink targetId="main-content" />

      <MobileHeader isMenuOpen={isMobileMenuOpen} onToggleMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)} />
      <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />

      {isWorkspaceRoute ? (
        <Suspense fallback={<PageLoader />}>
          <ConsumerWorkspace />
        </Suspense>
      ) : (
        <div className="app-container" style={{ '--sidebar-width': `${sidebarWidth}px` } as React.CSSProperties}>
          <aside
            className="sidebar"
            role="complementary"
            aria-label="Sidebar"
            style={{
              width: `${sidebarWidth}px`,
              transition: 'width 0.2s ease',
              padding: isCollapsed ? '1.5rem 0.5rem' : '1.5rem',
            }}
          >
            <NavLink
              to={APP_PATHS.root}
              className="logo-section"
              style={{
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: isCollapsed ? '0' : '0.75rem',
                justifyContent: isCollapsed ? 'center' : undefined,
                textDecoration: 'none',
                color: 'inherit',
              }}
            >
              {!isCollapsed ? (
                <span style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  letterSpacing: '-0.3px',
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                  color: '#F0F0F0',
                }}>
                  reeva<span style={{ color: '#F59E0B' }}>.</span>
                </span>
              ) : (
                <span style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: '#F0F0F0',
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                }}>
                  r<span style={{ color: '#F59E0B' }}>.</span>
                </span>
              )}
            </NavLink>

            <ModeSwitcher collapsed={isCollapsed} />
            <Navigation collapsed={isCollapsed} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {mode === 'individual' && (
                <button
                  onClick={toggleSidebar}
                  aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isCollapsed ? 'center' : undefined,
                    gap: '0.75rem',
                    padding: isCollapsed ? '0.625rem' : '0.625rem 1rem',
                    color: '#6b7280',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    width: '100%',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.05)'
                    e.currentTarget.style.color = '#9ca3af'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent'
                    e.currentTarget.style.color = '#6b7280'
                  }}
                >
                  {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
                  {!isCollapsed && <span>Collapse</span>}
                </button>
              )}

              <button
                onClick={async () => { await logout(); window.location.href = '/' }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: isCollapsed ? 'center' : undefined,
                  gap: '0.75rem',
                  padding: isCollapsed ? '0.75rem' : '0.75rem 1rem',
                  color: '#9ca3af',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  width: '100%',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.05)'
                  e.currentTarget.style.color = '#fff'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent'
                  e.currentTarget.style.color = '#9ca3af'
                }}
                title={isCollapsed ? 'Log out' : undefined}
              >
                <LogOut size={18} />
                {!isCollapsed && 'Log out'}
              </button>
            </div>
          </aside>

          <main
            id="main-content"
            className="main-content"
            tabIndex={-1}
            role="main"
            aria-label="Main content"
          >
            <AppRoutes />
          </main>
        </div>
      )}
    </>
  )
}
